// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricEntity, BaseMetricConfig, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for CommitteeMembers entity
 */
type CommitteeMembersConfig = BaseMetricConfig;

/**
 * CommitteeMembers Entity - Encapsulates all committee member-specific business logic
 *
 * This class consolidates configuration, scoring rules, and display logic
 * for the committee members ecosystem metric.
 */
export class CommitteeMembers extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'committee_members' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: CommitteeMembersConfig = {
    metricType: 'committee_members',
    display: {
      displayName: 'Committee Members',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of committee members are from our company?',
        scoringRules: [
          'Each 0.1% of committee members total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 committee members = 0 points',
          '0.000001 - 0.1% of committee members = 1.1 point',
          '0.2% of committee members = 1.2 points',
          '2.5% of committee members = 3.5 points',
          '20.0% of committee members = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: `Our company has no committee members for {context}.`,
      },
      positiveValue: {
        descriptionTemplate: `Employees from our company make up {percentage}% of all committee members for {context}.`,
      },
      nullValue: {
        description: this.NULL_VALUE_DESCRIPTION,
      }
    }
  };

  /**
   * Get display configuration from config object
  */
  public static getDisplayConfig(): BaseMetricConfig['display'] {
      return this.CONFIG.display;
  }

  /**
   * Get full configuration object
   */
  public static getConfig(): BaseMetricConfig {
      return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for committee members
   */
  static getBottomSummary(percentage: string, allTimeTotal: number, isLfProject: boolean, level: ScoreLevel, projectType?: string | null): MetricBottomSummary {
    if (isLfProject === false) {
      return this.getNonLfProjectBottomSummary(this.CONFIG);
    }

    if (allTimeTotal === 0) {
      return this.getNullValueBottomSummary(this.CONFIG);
    }

    const percentageValue = percentage || '0.0';
    const numericValue = parseFloat(percentageValue);
    const styles = this.getStylesByLevel(level);
    const contextText = projectType?.toLowerCase() === 'foundation'
      ? 'this foundation'
      : 'this project\'s foundation';

    const description = numericValue === 0
      ? this.formatDescription(this.CONFIG.bottomSummary.zeroValue.description, {context: contextText})
      : this.formatDescription(this.CONFIG.bottomSummary.positiveValue.descriptionTemplate, {percentage: percentageValue, context: contextText});

    return {
      title: this.CONFIG.display.displayName,
      description: description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }


}
